import React from 'react';
import { FiLogOut, FiUser, FiHome } from 'react-icons/fi';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';

// Layout & UI Components
import { Header, Footer } from './components/layout';
import { Chatbot } from './components/chatbot';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { authService } from './services/authService';
import { LanguageProvider } from './contexts/LanguageContext'; 
import { HelmetProvider } from 'react-helmet-async';



// Pages - Publiques
import { Home, About, Portfolio, Contact, News } from './pages/public';

// Pages - Services S2I
import { AnalyseConseil, DeploiementSupport } from './pages/servicesS2I';

// Pages - Solutions (Imports groupés)
import {
  Backup, SolutionHCI, Serveur, Stockage, Switching,
  CloudComputing, CyberSecurity, Firewall, WAF,
  LoadBalancer, AntiSpamAntiVirus, AccessSwitching,
  CoreSwitching, DigitalSignage, MursEcrans, Printing,
  AccessPointWIFI, ControllerWIFI,
} from './pages/solutions';

// Pages - Authentification
import { Login, ActivateAccount, ForgotPassword, ResetPassword } from './pages/auth';

// Pages - Administration
import {
  AdminDashboard, AdminPartners, AdminClients, AdminAdmins,
  AdminProfile, AdminMessages, AdminProducts, AdminPortfolios
} from './pages/admin';

/**
 * AppContent gère la logique d'affichage conditionnelle (Header/Footer/AdminBar)
 */
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();

  // Détection du mode Admin
  const isUrlAdmin = location.pathname.startsWith('/admin');
  const isAuthenticated = authService.isAuthenticated();
  const isAdminView = isUrlAdmin && isAuthenticated;

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  return (
    <div className="App">
      {/* Header Public : Masqué si on est dans l'espace Admin connecté */}
      {!isAdminView && <Header />}

      {/* 🔐 BARRE D'ADMINISTRATION PREMIUM (NAVY & RED) */}
      {isAdminView && (
        <div className="admin-top-bar" style={{
          width: '100%', background: '#0f172a', display: 'flex',
          alignItems: 'center', padding: '0.8rem 2rem', gap: 24,
          position: 'sticky', top: 0, zIndex: 1000, color: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/images/logo.png" alt="S2I" style={{ height: 45, filter: 'brightness(0) invert(1)' }} />
            <div style={{ height: 25, width: 2, background: '#d90000' }}></div>
            <span style={{ fontWeight: 800, letterSpacing: 1 }}>ESPACE ADMIN</span>
          </div>

          <div style={{ flexGrow: 1 }}></div>

          <div className="admin-nav-btns" style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => navigate('/admin/profile')} className="btn btn-sm btn-outline-light d-flex align-items-center gap-2">
              <FiUser /> Mon Profil
            </button>
            <button onClick={() => navigate('/')} className="btn btn-sm btn-outline-danger d-flex align-items-center gap-2">
              <FiHome /> Voir le site
            </button>
            <button onClick={handleLogout} className="btn btn-sm btn-danger d-flex align-items-center gap-2">
              <FiLogOut /> Déconnexion
            </button>
          </div>
        </div>
      )}

      <main style={{ minHeight: '80vh' }}>
        <Routes>
          {/* 🌍 ROUTES PUBLIQUES */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/contact" element={<Contact />} />
       
          <Route path="/news" element={<News />} /> 


          {/* 🔐 ROUTES AUTHENTIFICATION */}
          <Route path="/login" element={<Login />} />
          <Route path="/activate-account" element={<ActivateAccount />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* 🛠️ ROUTES SOLUTIONS (Correspondance avec les liens du Header) */}
          <Route path="/solutions/infrastructure-datacenter" element={<Serveur />} />
          <Route path="/solutions/serveur" element={<Serveur />} />
          <Route path="/solutions/hci" element={<SolutionHCI />} />
          <Route path="/solutions/backup" element={<Backup />} />
          <Route path="/solutions/stockage" element={<Stockage />} />
          <Route path="/solutions/switching" element={<Switching />} />
          <Route path="/solutions/cloud-security" element={<CyberSecurity />} />
          <Route path="/solutions/cloud-computing" element={<CloudComputing />} />
          <Route path="/solutions/cyberSecurity" element={<CyberSecurity />} />
          <Route path="/solutions/firewall" element={<Firewall />} />
          <Route path="/solutions/WAF" element={<WAF />} />
          <Route path="/solutions/load-balancer" element={<LoadBalancer />} />
          <Route path="/solutions/antispam-antivirus" element={<AntiSpamAntiVirus />} />
          <Route path="/solutions/reseau" element={<AccessSwitching />} />
          <Route path="/solutions/access-switching" element={<AccessSwitching />} />
          <Route path="/solutions/core-switching" element={<CoreSwitching />} />
          <Route path="/solutions/digital-signage" element={<DigitalSignage />} />
          <Route path="/solutions/murs-ecrans" element={<MursEcrans />} />
          <Route path="/solutions/print-management" element={<Printing />} />
          <Route path="/solutions/access-point-wifi" element={<AccessPointWIFI />} />
          <Route path="/solutions/controller-wifi" element={<ControllerWIFI />} />

          {/* 💼 ROUTES SERVICES S2I */}
          <Route path="/services/analyse-conseil" element={<AnalyseConseil />} />
          <Route path="/services/implementation-support" element={<DeploiementSupport />} />

          {/* 🛡️ ROUTES ADMIN PROTÉGÉES */}
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/partners" element={<ProtectedRoute><AdminPartners /></ProtectedRoute>} />
          <Route path="/admin/clients" element={<ProtectedRoute><AdminClients /></ProtectedRoute>} />
          <Route path="/admin/products" element={<ProtectedRoute><AdminProducts /></ProtectedRoute>} />
          <Route path="/admin/portfolios" element={<ProtectedRoute><AdminPortfolios /></ProtectedRoute>} />
          <Route path="/admin/profile" element={<ProtectedRoute><AdminProfile /></ProtectedRoute>} />
          <Route path="/admin/messages" element={<ProtectedRoute><AdminMessages /></ProtectedRoute>} />

          {/* 👑 ROUTE SUPER ADMIN UNIQUEMENT */}
          <Route path="/admin/admins" element={
            <ProtectedRoute requiredRole="SUPER_ADMIN">
              <AdminAdmins />
            </ProtectedRoute>
          } />
        </Routes>
      </main>

      {/* Footer : Masqué en mode Admin */}
      {!isAdminView ? <Footer /> : (
        <footer style={{ textAlign: 'center', padding: '1.5rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: '0.85rem', color: '#64748b' }}>
          &copy; {new Date().getFullYear()} S2I - Système de Gestion Intégré. Tous droits réservés.
        </footer>
      )}

      <Chatbot />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider> {/* <--- ENVELOPPE TOUT ICI */}
      <Router>
        <AppContent />
      </Router>
    </LanguageProvider>
    </HelmetProvider>
  );
}
export default App;